/**
 * F-008 — Merge resolver.
 *
 * Drains MergeCandidate pairs from the collision router, removes both
 * planets, spawns the next-tier planet at their midpoint. Top-tier
 * collisions take BR-002 path: both planets removed, no spawn, bonus
 * score returned to the caller.
 */

import type { Planet } from "./planet.ts";
import type { MergeCandidate } from "./collisions.ts";
import { isMaxTier, getTier } from "./tier.ts";
import type { SimulationRegistry } from "./spawn.ts";

export interface MergeResult {
  /** Number of merges that produced a new planet. */
  ascensions: number;
  /** Number of top-tier removals (BR-002). */
  topTierBursts: number;
  /** Sum of score values awarded by this batch. */
  scoreDelta: number;
  /** New planets spawned during the batch. */
  spawned: Planet[];
}

export function resolveMerges(
  sim: SimulationRegistry,
  candidates: ReadonlyArray<MergeCandidate>,
): MergeResult {
  let ascensions = 0;
  let topTierBursts = 0;
  let scoreDelta = 0;
  const spawned: Planet[] = [];

  for (const c of candidates) {
    // Stale guards — a planet may already have been merged earlier in the
    // same batch (chained merge). Skip if either side is no longer active.
    if (!sim.planets.includes(c.a) || !sim.planets.includes(c.b)) {
      continue;
    }

    const midpoint = {
      x: (c.a.body.position.x + c.b.body.position.x) / 2,
      y: (c.a.body.position.y + c.b.body.position.y) / 2,
      z: (c.a.body.position.z + c.b.body.position.z) / 2,
    };

    sim.removePlanet(c.a);
    sim.removePlanet(c.b);

    if (isMaxTier(c.tierIndex)) {
      // BR-002 — top-tier collision: bonus + no spawn.
      const bonus = getTier(c.tierIndex).scoreValue * 2;
      scoreDelta += bonus;
      topTierBursts += 1;
      continue;
    }

    const nextTier = c.tierIndex + 1;
    const newPlanet = sim.spawnPlanet({
      tierIndex: nextTier,
      position: midpoint,
    });
    spawned.push(newPlanet);
    scoreDelta += getTier(nextTier).scoreValue;
    ascensions += 1;
  }

  return { ascensions, topTierBursts, scoreDelta, spawned };
}
